import React, { useState, useEffect, useContext } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import ReCAPTCHA from 'react-google-recaptcha';
import authService from '../services/authService';
import AuthContext from '../contexts/AuthContext';
import { GoogleLogin } from '@react-oauth/google';

const API_BASE_URL = process.env.REACT_APP_API_URL;
const RECAPTCHA_SITE_KEY = '6LdX8D8sAAAAAPSIGjWEG3FkyfRjQkaneqB6QdoE';

export default function SignUpScreen({ onBack, onSignUpComplete }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [backgroundImage, setBackgroundImage] = useState(null);
  const [passwordsMatch, setPasswordsMatch] = useState(null);
  const [showCaptchaError, setShowCaptchaError] = useState(false);
  const [emailValid, setEmailValid] = useState(null);
  const [showPasswordError, setShowPasswordError] = useState(false);
  const [showEmailError, setShowEmailError] = useState(false);
  const [modal, setModal] = useState({ visible: false, title: '', message: '', type: 'info' });

  const showAlert = (title, message) => {
    setModal({
      visible: true,
      title,
      message,
      type: 'info'
    });
  };
  
  // reCAPTCHA state
  const [recaptchaToken, setRecaptchaToken] = useState('');

  const { googleSignIn } = useContext(AuthContext);

  // Fetch login background from API for this business (ensures per-business isolation)
  useEffect(() => {
    const businessId = localStorage.getItem('businessId');
    if (businessId) {
      authService.getBusinessBackgroundImage(businessId).then((img) => {
        if (img) {
          setBackgroundImage(img);
        } else {
          setBackgroundImage(null);
        }
      }).catch(() => {
        setBackgroundImage(null);
      });
    }
  }, []);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSignUp();
    }
  };

  const handleSignUp = async () => {
    if (!name || !email || !password || !confirmPassword) {
      showAlert("Fields Required", "Please Fill All Fields");
      return;
    }

    if (name.length > 20) {
      showAlert("Invalid Name", "Name must be 20 characters or less.");
      return;
    }
    
    if (password !== confirmPassword) {
      setShowPasswordError(true);
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setShowEmailError(true);
      return;
    }

    // Basic password strength check
    if (password.length < 6) {
      showAlert("Invalid Password", "Password must be at least 6 characters long");
      return;
    }

    // reCAPTCHA validation
    if (!recaptchaToken) {
      setShowCaptchaError(true);
      return;
    }

    setLoading(true);
    
    
    try {
      const result = await authService.signup({ 
        name, 
        email, 
        password, 
        confirmPassword,
        recaptchaToken
      });
      
      console.log('✅ Signup response:', result);
      
      // Show email sent confirmation
      setEmailSent(true);
      
      // Check if email was actually sent
      const emailSent = result?.data?.emailSent !== false;
      const hasWarning = result?.warning || result?.emailError;
      
      // Show appropriate message based on email status
      if (!emailSent || hasWarning) {
        showAlert("Account Created - Email Issue", `Your account has been created successfully, but we could not send the verification email to ${email}.\n\n${result?.emailError || result?.warning || 'Email service may be temporarily unavailable.'}\n\nPlease contact support or try the "Resend Verification" option on the login page.`);
      } else {
        showAlert("Account Created Successfully", `A verification email has been sent to ${email}.\nPlease check your inbox (and spam folder) and click the verification link to activate your account.\n\nDidn't receive it? Check your spam folder or use "Resend Verification" on the login page.`);
      }
      
      // Call completion callback if provided
      if (onSignUpComplete) {
        onSignUpComplete({ email, emailSent });
      } else {
        // Go back to login screen
        onBack();
      }
    } catch (error) {
      console.error('❌ Signup error:', error);
      showAlert("Signup Failed", error.message || 'Please Try Again');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setLoading(true);
    
    try {
      const result = await googleSignIn(credentialResponse.credential);
      
      // Show success message for new users
      if (result.isNewUser) {
        showAlert("Welcome to Invexis!", "Your account has been created successfully using Google Sign-In.\nLet's complete your business profile setup.");
      }
      
      // Call completion callback
      if (onSignUpComplete && result?.user) {
        onSignUpComplete(result.user);
      } else {
        onBack(); // Go to dashboard/onboarding
      }
    } catch (error) {
      console.error('Google Sign-Up failed:', error);
      showAlert("Google Sign-Up Failed", error.message || 'Please try again');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = () => {
    showAlert("Google Sign-Up Cancelled", "Google Sign-Up was cancelled or failed. Please try again.");
  };

  if (emailSent) {
    return (
      <div className="center-wrapper">
        <div className="container-2">
          <span className="logo">📧</span>
          <span className="title">INVEXIS</span>
          <div className="form">
            <div className="header">Email Verification Required</div>
            <div className="instructions" style={{ textAlign: 'center', marginBottom: '20px' }}>
              We've sent a verification email to:
              <br />
              <strong>{email}</strong>
              <br /><br />
              Please check your inbox and click the verification link to activate your account.
              <br /><br />
              After verification, you can sign in and complete your profile setup.
            </div>
            <button className="signUpButton" onClick={onBack}>
              Go to Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {modal.visible && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '16px',
            padding: '28px',
            width: '100%',
            maxWidth: '400px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.15), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            textAlign: 'center',
            border: '2px solid #000'
          }}>
            <h3 style={{
              margin: '0 0 16px 0',
              fontSize: '20px',
              fontWeight: 'bold',
              color: '#000'
            }}>{modal.title}</h3>
            <p style={{
              margin: '0 0 24px 0',
              fontSize: '15px',
              color: '#4b5563',
              lineHeight: '1.6',
              whiteSpace: 'pre-line',
              textAlign: 'left'
            }}>{modal.message}</p>
            <button
              onClick={() => setModal({ visible: false, title: '', message: '', type: 'info' })}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#e74c3c',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}

      <div className="center-wrapper">
        <div className="container-2">
          <span className="logo"></span>
          <span className="title">INVEXIS</span>
          
          <div className="form">
            <div className="header">Create Account</div>
            <div className="instructions">Sign up to get started with Invexis</div>
            
            <input
              className="input"
              placeholder="Full Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={20}
            />
            <input
              className="input"
              placeholder="Email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setShowEmailError(false);
              }}
              type="email"
              autoCapitalize="none"
            />
            {/* Email validation message */}
            {showEmailError && (
              <div className="password-match-message no-match">
                Invalid email format
              </div>
            )}
            <div className="password-wrapper">
              <input
                className="input password-input"
                placeholder="Password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                className="toggle-password"
                onClick={(e) => {
                  e.preventDefault();
                  setShowPassword(!showPassword);
                }}
                type="button"
              >
                <FontAwesomeIcon icon={showPassword ? faEye : faEyeSlash} />
              </button>
            </div>
            
            <div className="password-wrapper">
              <input
                className="input password-input"
                placeholder="Confirm Password"
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  setShowPasswordError(false);
                }}
                onKeyPress={handleKeyPress}
              />
              <button
                className="toggle-password"
                onClick={(e) => {
                  e.preventDefault();
                  setShowConfirmPassword(!showConfirmPassword);
                }}
                type="button"
              >
                <FontAwesomeIcon icon={showConfirmPassword ? faEye : faEyeSlash} />
              </button>
            </div>
            
            {/* Dynamic password match indicator */}
            {showPasswordError && (
              <div className="password-match-message no-match">
                Password mismatch
              </div>
            )}
            
            {/* reCAPTCHA Section */}
            <div className="captcha-container">
              <div className="captcha-label">Security Verification</div>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '12px' }}>
                <ReCAPTCHA
                  sitekey={RECAPTCHA_SITE_KEY}
                  onChange={(token) => {
                    setRecaptchaToken(token);
                    setShowCaptchaError(false);
                  }}
                  onExpired={() => setRecaptchaToken('')}
                  onErrored={() => setRecaptchaToken('')}
                  theme="light"
                />
              </div>
              {showCaptchaError && (
                <div className="password-match-message no-match">
                  Please complete the security verification
                </div>
              )}
            </div>
            
            <button 
              className="signUpButton" 
              onClick={handleSignUp}
              disabled={loading}
            >
              {loading ? 'Creating Account...' : 'Sign Up'}
            </button>
            
            <span className="orText">or</span>
            
            {/* Google Sign-In Button */}
            <div style={{ 
              width: '100%', 
              display: 'flex', 
              justifyContent: 'center', 
              marginBottom: '16px' 
            }}>
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                theme="outline"
                size="large"
                text="signup_with"
                shape="rectangular"
                width="100%"
              />
            </div>
            
            <button className="backText" onClick={onBack}>
              Already have an account? Sign In
            </button>
          </div>
        </div>
      </div>
      <style>
        {`
          .center-wrapper {
            height: 100vh !important;
            min-height: unset !important;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #f7f7f7;
          }
          .container-2 {
            max-width: ${backgroundImage ? '600px' : 'none'};
            width: 100vw;
            height: 100vh !important;
            min-height: unset !important;
            padding: 20px 20px 0 20px;
            font-family: Arial, sans-serif;
            background: ${backgroundImage ? `url('${backgroundImage}')` : "#fff"} no-repeat center center;
            background-size: cover;
            background-position: center;
            position: relative;
            --invexis-input-bg: rgba(255, 255, 255, 0.9);
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            margin: 0 auto;
          }
          @media (max-width: 768px) {
            .container-2 {
              background-attachment: scroll;
              background-size: cover;
              overflow: auto;
            }
          }

          .logo {
            font-size: 48px;
            margin-bottom: 8px;
          }

          .title {
            color: black;
            font-size: 36.8px;
            font-weight: bold;
            letter-spacing: 2px;
            margin-bottom: 32px;
          }

          .header {
            font-size: 24px;
            font-weight: bold;
            color: #000;
            margin-bottom: 8px;
            text-align: left;
          }
          .instructions {
            color: #555;
            font-size: 16px;
            margin-bottom: 16px;
            text-align: left;
          }

          .form {
            background-color: rgba(255, 255, 255, 0.95);
            padding: 30px;
            border-radius: 16px;
            width: 100%;
            max-width: 400px;
            margin: 0 auto;
            display: flex;
            flex-direction: column;
            align-items: stretch;
            box-shadow: 0 8px 32px rgba(0,0,0,0.2);
          }

          .input {
            height: 2.8rem;
            border: 2px solid #000 !important;
            border-radius: 8px;
            margin-bottom: 16px;
            padding: 0 12px;
            background-color: rgba(255, 255, 255, 0.9);
            font-size: 16px;
            color: black;
            width: 100%;
            box-sizing: border-box;
            outline: none;
            transition: none !important;
            transform: none !important;
          }

          .input:-webkit-autofill,
          .input:-webkit-autofill:hover,
          .input:-webkit-autofill:focus,
          .input:-webkit-autofill:active,
          .password-input:-webkit-autofill,
          .password-input:-webkit-autofill:hover,
          .password-input:-webkit-autofill:focus,
          .password-input:-webkit-autofill:active,
          .captcha-input:-webkit-autofill,
          .captcha-input:-webkit-autofill:hover,
          .captcha-input:-webkit-autofill:focus,
          .captcha-input:-webkit-autofill:active {
            background-color: rgba(255, 255, 255, 0.9) !important;
            -webkit-box-shadow: 0 0 0 1000px rgba(255, 255, 255, 0.9) inset !important;
            box-shadow: 0 0 0 1000px rgba(255, 255, 255, 0.9) inset !important;
            -webkit-text-fill-color: #000 !important;
            caret-color: #000 !important;
            border: 2px solid #000 !important;
            transition: background-color 999999s ease-out 0s !important;
          }

          .input:-moz-autofill,
          .password-input:-moz-autofill,
          .captcha-input:-moz-autofill {
            box-shadow: 0 0 0 1000px rgba(255, 255, 255, 0.9) inset !important;
            background-color: rgba(255, 255, 255, 0.9) !important;
            color: #000 !important;
          }

          .input:focus {
            border: 2px solid #000 !important;
            outline: none;
            transition: none !important;
            transform: none !important;
          }

          .input::placeholder {
            color: #black;
          }

          .password-wrapper {
            position: relative;
            width: 100%;
            margin: 0.1rem 0 0.8rem 0;
            border: 2px solid #000;
            border-radius: 8px;
            background-color: transparent;
          }

          .password-input {
            padding-right: 40px;
            margin-bottom: 0;
            border: none !important;
            background-color: transparent;
            outline: none;
            transition: none !important;
            transform: none !important;
          }

          .password-input:focus {
            border: none !important;
            outline: none;
            transition: none !important;
            transform: none !important;
          }

          .toggle-password {
            position: absolute;
            right: 12px;
            top: 50%;
            transform: translateY(-50%);
            background: none;
            border: none;
            cursor: pointer;
            font-size: 18px;
            color: #888;
            padding: 4px;
            line-height: 1;
            width: 24px;
            height: 24px;
            display: flex;
            align-items: center;
            justify-content: center;
            outline: none !important;
            user-select: none;
            -webkit-tap-highlight-color: transparent;
            transition: none;
            pointer-events: auto;
          }

          .toggle-password * {
            pointer-events: none;
          }

          .toggle-password:hover {
            color: #000;
          }

          .toggle-password:focus {
            outline: none !important;
            box-shadow: none !important;
          }

          .toggle-password:active {
            outline: none !important;
            box-shadow: none !important;
            transform: translateY(-50%);
          }

          /* Override global smoothTransitions.css for auth screen */
          .container-2 input,
          .container-2 button {
            transition: none !important;
          }
          .container-2 input:focus,
          .container-2 input:active,
          .container-2 button:not(.toggle-password):active {
            transform: none !important;
          }
          .container-2 .toggle-password {
            transform: translateY(-50%) !important;
          }
          .container-2 .toggle-password:active {
            transform: translateY(-50%) !important;
          }

          .signUpButton {
            background-color: #e74c3c;
            color: #fff;
            padding: 14px 0;
            border-radius: 8px;
            font-size: 16px;
            font-weight: bold;
            text-align: center;
            margin-bottom: 8px;
            cursor: pointer;
            border: none;
          }

          .signUpButton:hover {
            background-color: #e74c3c;
          }

          .signUpButton:disabled {
            background-color: #999;
            cursor: not-allowed;
          }

          .orText {
            text-align: center;
            margin: 16px 0;
            color: #666;
            font-size: 14px;
            font-weight: 500;
          }

          .backText {
            color: #222;
            text-align: center;
            margin-top: 8px;
            text-decoration: underline;
            font-size: 15px;
            background: none;
            border: none;
            cursor: pointer;
          }

          .captcha-container {
            margin-bottom: 16px;
          }

          .captcha-label {
            font-size: 14px;
            font-weight: 600;
            color: #333;
            margin-bottom: 8px;
          }

          .captcha-box {
            position: relative;
            border: 2px solid #000;
            border-radius: 8px;
            background: #f7f9fc;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-bottom: 12px;
          }

          .captcha-svg {
            flex: 1;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .captcha-svg svg {
            width: 100%;
            height: auto;
            max-width: 200px;
          }

          .captcha-loading {
            color: #666;
            font-style: italic;
          }

          .captcha-refresh {
            position: absolute;
            right: 10px;
            top: 50%;
            transform: translateY(-50%);
            background: #e74c3c;
            border: none;
            border-radius: 50%;
            width: 32px;
            height: 32px;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            color: white;
            font-size: 14px;
            transition: background-color 0.2s;
          }

          .captcha-refresh:hover {
            background: #c0392b;
          }

          .captcha-refresh:disabled {
            background: #999;
            cursor: not-allowed;
          }

          .captcha-input {
            text-align: center;
            font-size: 18px;
            letter-spacing: 2px;
          }

          /* Password match indicator */
          .password-match-message {
            font-size: 13px;
            font-weight: 500;
            margin-bottom: 12px;
            margin-top: -8px;
            text-align: left;
            transition: all 0.3s ease;
          }

          .password-match-message.no-match {
            color: #d32f2f;
          }

          /* Fix reCAPTCHA error message styling */
          div.rc-anchor-error-msg-container {
            white-space: nowrap !important;
            overflow: visible !important;
            width: auto !important;
            max-width: none !important;
          }

          div.rc-anchor-error-msg {
            white-space: nowrap !important;
            overflow: visible !important;
            font-size: 11px !important;
          }

          /* Hide original text and replace with custom message */
          div.rc-anchor-error-msg span {
            font-size: 0 !important;
          }

          div.rc-anchor-error-msg span::before {
            content: "Verification expired. Please check the checkbox." !important;
            font-size: 11px !important;
            white-space: nowrap !important;
            display: inline !important;
          }
        `}
      </style>
    </>
  );
}
